import { useTranslation } from "react-i18next";
import { CustomInput } from "../atoms/CustomInput";
import { useNavigate, useParams } from "react-router-dom";
import { CustomButton } from "../atoms/CustomButton";
import { useForm } from "react-hook-form";
import { useUserLogin, useUserRegister } from "../../hooks/users";
import { useLmcUserView } from "../../contexts/AuthContext";
import { useState } from "react";
import { toast } from "react-toastify";
import LmcVerifyEmail from "../pages/lmcUserVIew/components/LmcVerifyEmail";
import { lmcInputClass, lmcFieldClass } from "../../utils/utils";

interface LoyaltyEmailLoginProps {
  onLoginSuccess?: () => void;
  onForgotPassword?: () => void;
}

export default function LoyaltyEmailLogin({
  onLoginSuccess,
  onForgotPassword,
}: LoyaltyEmailLoginProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("clientView");
  const {
    state: {
      entity: { entity_data },
      auth,
    },
    dispatch,
  } = useLmcUserView();
  const { processId } = useParams();
  const { mutate: login, isLoading } = useUserLogin();
  const { mutate: registerUser } = useUserRegister();

  const [showConfirmEmail, setShowConfirmEmail] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState({
    user_email: "",
    user_identification: "",
    user_password: "",
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<{ user_email: string; user_password: string }>();

  const onSubmit = (data: { user_email: string; user_password: string }) => {
    login(
      {
        entity_identification: entity_data?.entity_identification,
        data: data,
      },
      {
        onSuccess(result, variables, context) {
          const hasAccessToken = result.data.element.access_token;

          if (!hasAccessToken) {
            setUserInfo({
              user_email: data.user_email,
              user_identification: result.data.element.user_identification,
              user_password: data.user_password,
            });
            setShowConfirmEmail(true);
            return;
          }

          setShowConfirmEmail(false);

          if (hasAccessToken || auth.skip_gaming) {
            dispatch({
              type: "LOGIN",
              payload: {
                ...result.data.element,
              },
            });
          }
          if (onLoginSuccess) {
            onLoginSuccess();
          } else if (processId) {
            navigate(`/loyalty-program/${processId}/dashboard`, {
              replace: true,
              state: {
                user_email: data.user_email,
                user_password: data.user_password,
              },
            });
          }
        },
        onError(error: any, variables, context) {
          const code = error.response?.data?.code;
          if (code && code === "RSUSKO015") {
            register(data);
          } else if (code && (code === "RSUSOK006" || code === "RSUSKO014")) {
            setUserInfo({
              user_email: data.user_email,
              user_identification:
                error.response.data.element.user_identification,
              user_password: data.user_password,
            });

            setShowConfirmEmail(true);
          }
          toast.error(
            code && code === "RSUSKO029"
              ? t("key61")
              : error.response?.data.message || error.message
          );
        },
      }
    );
  };

  const register = (data: any) => {
    registerUser(
      {
        entity_identification: entity_data?.entity_identification,
        data: data,
      },
      {
        onSuccess: () => {
          setShowConfirmEmail(true);
        },
      }
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="w-full ">
          <CustomInput
            name="user_email"
            inputDir="ltr"
            type="email"
            control={control}
            error={errors.user_email}
            helperText={
              errors.user_email?.type === "required"
                ? (t("key22") as string)
                : errors.user_email?.type === "pattern"
                ? (t("key23") as string)
                : ""
            }
            label={t("key21") as string}
            grayBackground
            rules={{
              required: true,
              pattern: /[^@ \t\r\n]+@[^@ \t\r\n]+\.[^@ \t\r\n]+/,
            }}
            className={lmcInputClass}
            inputClassName={lmcFieldClass}
            autoComplete="email"
          />
        </div>

        <div className="w-full my-4">
          <CustomInput
            name="user_password"
            control={control}
            error={errors.user_password}
            helperText={
              errors.user_password?.type === "required"
                ? (t("key64") as string)
                : errors.user_password?.type === "pattern"
                ? (t("key65") as string)
                : ""
            }
            rules={{
              required: true,
              pattern: /^[^\s]{8,32}$/,
            }}
            type="password"
            label={t("key50") as string}
            className={lmcInputClass}
            inputClassName={lmcFieldClass}
            autoComplete="current-password"
          />
          {onForgotPassword && (
            <div className="flex justify-end mt-1">
              <button
                type="button"
                onClick={onForgotPassword}
                className="text-blue-400 text-xs hover:text-blue-300 transition"
              >
                {t("key57")}
              </button>
            </div>
          )}
        </div>

        <div className="w-full pt-4">
          <CustomButton
            type="submit"
            variant="primary"
            className="w-full"
            disabled={isLoading}
            isLoading={isLoading}
            title={t("key63") as string}
          />
        </div>
      </form>
      <LmcVerifyEmail
        userEmail={userInfo.user_email}
        userPassword={userInfo.user_password}
        userId={userInfo.user_identification}
        showConfirmEmailModal={showConfirmEmail}
        handleLogin={() => {
          setShowConfirmEmail(false);
          onSubmit({
            user_email: userInfo.user_email,
            user_password: userInfo.user_password,
          });
        }}
        isLoading={isLoading}
      />
    </>
  );
}
